import { useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatMessage } from './ChatMessage';
import { WelcomeScreen } from './WelcomeScreen';
import { AlertIcon } from './icons';
import { VisualAskContext } from './visuals/visualContext.js';
import { repeatedCompactions } from '../utils/contextNotes.js';
import './ChatContainer.css';

export function ChatContainer({ messages, isLoading, error, onRate, onRegenerate, onEdit, onSuggestion, onSituation = null, effortSupported = true, loadingConversation = false }) {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  // Suivre le bas de la conversation, sauf si l'utilisateur est remonté lire
  const stickToBottomRef = useRef(true);
  const messageCount = messages.length;

  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Nouveau message : on revient en bas en douceur (pas sur l'accueil, qui reste en haut)
  useEffect(() => {
    if (messageCount === 0) {
      if (containerRef.current) containerRef.current.scrollTop = 0;
      return;
    }
    stickToBottomRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messageCount]);

  // Texte en cours d'arrivée : défilement immédiat (le défilement doux saccade à chaque token)
  useEffect(() => {
    if (!stickToBottomRef.current || messageCount === 0) return;
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const showEmptyState = messages.length === 0 && !isLoading && !loadingConversation;

  // « Détails » d'une tuile KPI : la question de détail est posée dans la conversation
  const visualAsk = useMemo(() => ({ ask: onSuggestion ?? null, busy: isLoading }), [onSuggestion, isLoading]);

  // Résumé réutilisé d'une réponse à l'autre : un seul marqueur « Contexte condensé » dans le fil
  const hiddenCompactions = useMemo(() => repeatedCompactions(messages), [messages]);

  return (
    <main
      className={`chat-container ${showEmptyState ? 'is-empty' : ''}`}
      ref={containerRef}
      onScroll={onScroll}
      aria-label="Conversation"
      aria-busy={loadingConversation || undefined}
      tabIndex={-1}
    >
      {loadingConversation && messages.length === 0 && (
        <div className="chat-loading" role="status">
          <span className="chat-loading__line" aria-hidden="true" />
          <span className="chat-loading__line is-short" aria-hidden="true" />
          <span className="chat-loading__line" aria-hidden="true" />
          <span className="sr-only">Chargement de la conversation…</span>
        </div>
      )}

      {showEmptyState && <WelcomeScreen onSuggestion={onSuggestion} onSituation={onSituation} effortSupported={effortSupported} disabled={isLoading} />}

      {(!showEmptyState || error) && (
        <VisualAskContext.Provider value={visualAsk}>
        <div className="chat-thread">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id ?? index}
                message={message}
                index={index}
                onRate={onRate}
                onRegenerate={onRegenerate}
                onEdit={onEdit}
                isLast={index === messages.length - 1}
                hasFollowing={index < messages.length - 2}
                busy={isLoading}
                showCompaction={!hiddenCompactions.has(index)}
              />
            ))}
          </AnimatePresence>

          {error && (
            <motion.div
              className="error-message"
              role="alert"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <span className="error-icon" aria-hidden="true">
                <AlertIcon size={18} />
              </span>
              <div className="error-content">
                <strong>Un problème est survenu</strong>
                <span>{error}</span>
              </div>
            </motion.div>
          )}
        </div>
        </VisualAskContext.Provider>
      )}

      <div ref={messagesEndRef} />
    </main>
  );
}
